import React, { useCallback } from 'react';

import { Box, ColorInput, Flex, FlexProps, Space, Switch, Text, TextInput, Textarea } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { CharacterCounter } from 'components/CharacterCounter';
import { EditAvatar } from 'components/EditAvatar';
import { GradientColorPicker } from 'components/SmartAssistantSections/CustomizationSection/GradientColorPIcker';
import { SmartAssistantPreviewer } from 'components/SmartAssistantSections/CustomizationSection/SmartAssistantPreviewer';
import { useSmartAssistantAvatarUrlHandling } from 'hooks/use-smart-assistant-avatar-url-handling';
import { useSmartAssistantVisualConfigExtractor } from 'hooks/use-smart-assistant-theme-extractor';
import { useTranslation } from 'react-i18next';
import { SMART_ASSISTANT_COLOR_THEMES } from 'shared/constants/smart-assistant';
import { MAX_WIDTH } from 'shared/constants/theme';
import { CustomizationFormValues, SmartAssistant, SmartAssistantColorTheme } from 'shared/interfaces/SmartAssistant';
import { SMART_ASSISTANT_WELCOME_MESSAGE_MAX_LENGTH } from 'shared/utils/form-validators';

interface CustomizationSectionProps extends FlexProps {
  form: UseFormReturnType<CustomizationFormValues>;
  smartAssistant: SmartAssistant;
}

export function CustomizationSection({ form, smartAssistant, ...flexProps }: CustomizationSectionProps) {
  const { t } = useTranslation();
  const { backToDefaultAvatar, handleImageDrop, isUpdatingAvatar } = useSmartAssistantAvatarUrlHandling(smartAssistant);

  const smartAssistantVisualConfig = useSmartAssistantVisualConfigExtractor({
    customizationForm: form,
    avatarUrl: smartAssistant.avatarUrl
  });

  const isThemeSelected = (theme: SmartAssistantColorTheme) =>
    form.values.assistantMessageColor === theme.assistantMessageColor &&
    form.values.userMessageColor === theme.userMessageColor &&
    form.values.conversationBackgroundColor === theme.conversationBackgroundColor &&
    form.values.secondaryBackgroundColor === theme.secondaryBackgroundColor &&
    form.values.gradientColors?.length === theme.gradientColors?.length &&
    form.values.gradientColors?.every((color, index) => color === theme.gradientColors?.[index]);

  const onThemeSelect = (theme: SmartAssistantColorTheme) => {
    form.setFieldValue('assistantMessageColor', theme.assistantMessageColor);
    form.setFieldValue('userMessageColor', theme.userMessageColor);
    form.setFieldValue('conversationBackgroundColor', theme.conversationBackgroundColor);
    form.setFieldValue('secondaryBackgroundColor', theme.secondaryBackgroundColor);
    form.setFieldValue('gradientColors', theme.gradientColors);
  };

  const isThemeEnabled = () =>
    form.values.assistantMessageColor !== undefined &&
    form.values.userMessageColor !== undefined &&
    form.values.conversationBackgroundColor !== undefined &&
    form.values.secondaryBackgroundColor !== undefined &&
    form.values.gradientColors !== undefined;

  const getPreviewMessages = useCallback(() => {
    let previewMessages = [
      {
        text: t(`SMART_ASSISTANT.PREVIEW.USER_MESSAGE`),
        fromUser: true
      },
      {
        text: t(`SMART_ASSISTANT.PREVIEW.SMART_ASSISTANT_MESSAGE`),
        fromUser: false
      }
    ];
    previewMessages = form.values.welcomeMessage
      ? [{ text: form.values.welcomeMessage, fromUser: false }, ...previewMessages]
      : previewMessages;

    return previewMessages;
  }, [t, form.values.welcomeMessage]);

  return (
    <Flex
      w="100%"
      direction={{ base: 'column', lg: 'row' }}
      align="stretch"
      justify="space-between"
      gap="xl"
      maw={MAX_WIDTH}
      {...flexProps}>
      <Box maw={500} w="100%">
        <Flex w="100%" direction="row" align="flex-start">
          <EditAvatar
            src={smartAssistant.avatarUrl}
            size={50}
            radius={25}
            onRemove={backToDefaultAvatar}
            onFileDrop={handleImageDrop}
            loading={isUpdatingAvatar}
            mt={6}
          />
          <Space w="sm" />
          <TextInput
            w="100%"
            label={t('SMART_ASSISTANT.NAME.LABEL')}
            placeholder={t('SMART_ASSISTANT.NAME.PLACEHOLDER')}
            {...form.getInputProps('name')}
          />
        </Flex>

        {form.values.welcomeMessage !== undefined && (
          <>
            <Space h="md" />

            <Textarea
              minRows={4}
              label={t('SMART_ASSISTANT.WELCOME_MESSAGE.LABEL')}
              maxRows={4}
              placeholder={t('SMART_ASSISTANT.WELCOME_MESSAGE.PLACEHOLDER')}
              maxLength={SMART_ASSISTANT_WELCOME_MESSAGE_MAX_LENGTH}
              {...form.getInputProps('welcomeMessage')}
            />
            <CharacterCounter
              length={form.values.welcomeMessage.length}
              maxLength={SMART_ASSISTANT_WELCOME_MESSAGE_MAX_LENGTH}
            />
          </>
        )}

        <Space h="md" />
        <Switch
          styles={(theme) => ({
            label: { color: theme.other.text.default }
          })}
          label={t('SMART_ASSISTANT.SHOW_ASSISTANT_BADGE')}
          checked={form.values.showBadge}
          {...form.getInputProps('showBadge')}
        />

        {isThemeEnabled() && (
          <>
            <Space h="md" />

            <Text>{t('SMART_ASSISTANT.THEMES')}</Text>
            <Flex direction="row" align="center">
              {SMART_ASSISTANT_COLOR_THEMES.map((smartAssistantTheme, key) => (
                <Box
                  mr={5}
                  key={key}
                  w={30}
                  h={30}
                  onClick={() => onThemeSelect(smartAssistantTheme)}
                  sx={(theme) => ({
                    borderRadius: '30px',
                    border: `1px solid ${isThemeSelected(smartAssistantTheme) ? theme.colors.dark[9] : 'transparent'}`,
                    cursor: 'pointer'
                  })}>
                  <Box
                    m="auto"
                    mt={1.5}
                    w={25}
                    h={25}
                    sx={{
                      borderRadius: '30px',
                      background: smartAssistantTheme.userMessageColor
                    }}></Box>
                </Box>
              ))}
            </Flex>
          </>
        )}

        {form.values.assistantMessageColor !== undefined && (
          <>
            <Space h="md" />

            <ColorInput
              label={t('SMART_ASSISTANT.SM_COLOR')}
              format="hexa"
              {...form.getInputProps('assistantMessageColor')}
              value={form.values.assistantMessageColor.toUpperCase()}
              onChange={undefined}
              onChangeEnd={(color) => {
                form.setFieldValue('assistantMessageColor', color);
              }}
            />
          </>
        )}

        {form.values.userMessageColor !== undefined && (
          <>
            <Space h="md" />

            <ColorInput
              label={t('SMART_ASSISTANT.USER_COLOR')}
              format="hexa"
              {...form.getInputProps('userMessageColor')}
              value={form.values.userMessageColor.toUpperCase()}
              onChange={undefined}
              onChangeEnd={(color) => {
                form.setFieldValue('userMessageColor', color);
              }}
            />
          </>
        )}

        {form.values.conversationBackgroundColor !== undefined && (
          <>
            <Space h="md" />

            <ColorInput
              label={t('SMART_ASSISTANT.CONVERSATION_BG_COLOR')}
              format="hexa"
              {...form.getInputProps('conversationBackgroundColor')}
              value={form.values.conversationBackgroundColor.toUpperCase()}
              onChange={undefined}
              onChangeEnd={(color) => {
                form.setFieldValue('conversationBackgroundColor', color);
              }}
            />
          </>
        )}

        {form.values.secondaryBackgroundColor !== undefined && (
          <>
            <Space h="md" />

            <ColorInput
              label={t('SMART_ASSISTANT.SECONDARY_BG_COLOR')}
              format="hexa"
              {...form.getInputProps('secondaryBackgroundColor')}
              value={form.values.secondaryBackgroundColor.toUpperCase()}
              onChange={undefined}
              onChangeEnd={(color) => {
                form.setFieldValue('secondaryBackgroundColor', color);
              }}
            />
          </>
        )}

        {form.values.gradientColors !== undefined && (
          <>
            <Space h="md" />

            <GradientColorPicker
              label={t('SMART_ASSISTANT.GRADIENT_COLOR')}
              colors={form.values.gradientColors}
              onChange={(colors) => {
                form.setFieldValue('gradientColors', colors);
              }}
              error={form.errors.gradientColors}
            />
          </>
        )}
      </Box>

      {isThemeEnabled() && (
        <SmartAssistantPreviewer
          maw={800}
          mt="md"
          h={undefined}
          style={{ flexGrow: 1 }}
          smartAssistantVisualConfig={smartAssistantVisualConfig}
          messages={getPreviewMessages()}
        />
      )}
    </Flex>
  );
}
